// This is the top level container for the SaaS VR App.
// It's purpose is to wire up the React & Redux modules, and perform an initial authentication check,
// A lot of this functionality will probably be delegated to CUP when that integration happens.


// load polyfills
import 'core-js';
import 'es6-object-assign/auto';
import 'date-utils';
// TODO: Fix webpack not liking this
// import './utils/deepClone';

import Logger, {LOG_LEVELS} from './utils/logger';
import MESSAGE_LIST_CLIENT from './utils/inAppMessagesDictionaryClient'
import getClientConfig from './utils/getClientConfig';
import REST_CLIENT from './utils/restClient';

// Load ReactJS ecosystem
import React, { Component } from 'react';
import { Provider } from 'react-redux';
import { push, ConnectedRouter as Router } from 'react-router-redux'
import AUTHENTICATION_MODULE  from './controllers/authentication';
import { ACTION_TYPES }  from './controllers/featureFlag';
import NAVIGATION_MENU_VISIBILITY  from './controllers/navigationMenuVisibility';
import setupGlobalAppState, { browserHistory } from './globalAppStateSetup';

// Import the components which are routed to.
import Header from './components/page/Header';
import Footer from './components/page/Footer';
import AppConfig from './utils/appConfig';

import { Route, Switch } from 'react-router-dom';
import LoggedOutRouter from './components/routing/LoggedOut';
import LogoutPage from './components/oidc/LogoutPage';
import BusySpinner from './components/spinners/BusySpinner';
import KeepAlive from './components/shared/KeepAlive';
import { ReadOnlyProvider } from './contexts/ReadOnlyContext';

import { setSessionData } from './controllers/session'

// Load font management stuff
import FontFaceObserver from 'fontfaceobserver';

import { jwtDecode } from "jwt-decode";
import { getAccessToken } from './utils/tokenStorage';
import { AuiSessionTimeout } from '@avalara/skylab-sdk/react';
import SideNav from './components/page/SideNav';
import AviChatbotContainer from './components/aviChatBot/avi-chatbot-container';

// Set up a logger
const log = new Logger('SAAS_VR_WEB_APP', LOG_LEVELS.DEBUG);

// Set up the font management stuff
const openSansObserver = new FontFaceObserver('Open Sans');
openSansObserver.load().then(() => {
  document.body.classList.add('js-open-sans-loaded');
}, (err) => {
  document.body.classList.remove('js-open-sans-loaded');
});

const store = setupGlobalAppState();
const { enableAvalaraIdentity , enableAvalaraIdentityJwt, aiEndSessionUrl, enableIdleTimeout } = getClientConfig();

class SaaSVrWebApp extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loggedIn: undefined,
      displayLanguage: 'EN',
      products: [],
      hideNavigationMenu: false
    };
  }
  
  renderSideNavMainContent() {
    const { loggedIn, displayLanguage, products, hideNavigationMenu } = this.state;
    const loading = loggedIn ? true : false;

    if (loggedIn === undefined) {
      return <BusySpinner busy={loading}/>;
    }

    if (loggedIn) {
      return (
        <SideNav 
          onLogoutSuccess={this.onLogoutSuccess.bind(this)}
          displayLanguage={displayLanguage}
          products={products}
          loggedIn={loggedIn}
          hideMenu={hideNavigationMenu}
        />
      );
    }

    return (
      <LoggedOutRouter
        displayLanguage={displayLanguage}
        onLogin={this.onAuthenticated.bind(this)}
        onLoginFail={this.onAuthenticationFailed.bind(this)}
      />
    );
  }

  render() {
    log.debug('render(): ', this.props, this.state);
    log.debug('render(): AppConfig: ', AppConfig);
    
    return (
      <Provider store={store}>
        <ReadOnlyProvider>
          <Router history={browserHistory}>
              <div className="site-container">
                <Header
                  onLogoutSuccess={this.onLogoutSuccess.bind(this)}
                  displayLanguage={this.state.displayLanguage}
                  products={this.state.products}
                  loggedIn={this.state.loggedIn}
                  hideMenu ={this.state.hideNavigationMenu}
                />
                <Switch>
                  <Route exact path="/auth/logout" component={LogoutPage} />
                  <Route render={() => (
                    (this.renderSideNavMainContent())
                  )} />
                </Switch>
                {enableAvalaraIdentity || enableAvalaraIdentityJwt && <KeepAlive
                  tokenRefreshWindowInMilliseconds={500}
                />}
                {enableIdleTimeout && enableAvalaraIdentityJwt && this.state.loggedIn && (
                  <AuiSessionTimeout
                    onS-timeout={this.handleIdleTimeout}
                    onS-sessionextend={() => {}}
                  />
                )}
                <AviChatbotContainer />
                {this.state.hideNavigationMenu ? (<div></div>):(<Footer/>)}
              </div>
          </Router>
        </ReadOnlyProvider>
      </Provider>
    )
  }
  UNSAFE_componentWillMount() {
    const token = sessionStorage.token;
    log.debug('componentWillMount(): ', token, this.state, this.props);

    if (this.state.loggedIn !== false && enableAvalaraIdentityJwt && !this.isJwtValid()) {
      this.setState({loggedIn: false});
      return;
    }

    if (!enableAvalaraIdentityJwt && !token && !window.auiUser.isLoggedIn && this.state.loggedIn) {
      // Note: this causes several unnecessary re-renders if loggedIn is set
      // to false when already false
      this.setState({loggedIn: false});
      return;
    }

    store.dispatch(AUTHENTICATION_MODULE.doAttemptTokenAuthentication(
      this.onAuthenticated.bind(this),
      this.onAuthenticationFailed.bind(this),
    ));
  }

  componentDidMount() {
    if (!this.state.loggedIn) {
      return;
    }

    if (enableAvalaraIdentityJwt && !this.isJwtValid()) {
      this.setState({loggedIn: false});
      return;
    }

    store.dispatch(
      AUTHENTICATION_MODULE.doAttemptTokenAuthentication(
        this.onAuthenticated.bind(this),
        this.onAuthenticationFailed.bind(this)
      )
    );
    let hideNavigationMenu = browserHistory.location && browserHistory.location.search.includes(AppConfig.EnablePartnerViewQueryString.QUERY_STRING);
    if (hideNavigationMenu){
      this.setState({ hideNavigationMenu : hideNavigationMenu });
    }
    store.dispatch(NAVIGATION_MENU_VISIBILITY.setNavigationMenuVisibility(hideNavigationMenu));

    if (enableAvalaraIdentity || enableAvalaraIdentityJwt) {
      store.dispatch(setSessionData({...window.auiSession, setAt: new Date() }));
    }
  }

  onAuthenticated(authInfo) {
    log.debug('onAuthenticated(): ', authInfo);
    sessionStorage.token = authInfo.token;

    let products = authInfo.products || [];

    this.setState({ loggedIn: true,
      displayLanguage: authInfo.preferredLanguage,
      products: products
    });

    REST_CLIENT.getFeatureFlags()
      .then((response) => {
        // this transformation logic should ideally be in the BFF or FF service
        window.appConfig.featureFlags = response.reduce((flags, flag) => {
          flags[flag.name] = flag.value;
          return flags;
        }, {});
        store.dispatch({
          type: ACTION_TYPES.LOAD_FEATURE_FLAGS,
          payload: window.appConfig.featureFlags
        });
      })
      .catch((error) => {
        window.appConfig.featureFlags = {};
        store.dispatch({
          type: ACTION_TYPES.LOAD_FEATURE_FLAGS,
          payload: window.appConfig.featureFlags
        });
        log.debug('onAuthenticated.getFeatureFlags: failed to get the feature flags', error);
      });

    // Runs alongside the feature flags rather than blocking auth.
    store.dispatch(AUTHENTICATION_MODULE.doLoadEntitlements());

    this.props.onAuthenticationComplete(null, authInfo);
    MESSAGE_LIST_CLIENT.loadErrorDictionary()
      .then((errorDictionary) => {
        log.debug('onAuthenticated.loadErrorDictionary: got the error dictionary', errorDictionary);
        Logger.updateErrorDictionary(errorDictionary);
      })
      .catch((errorResponse) => {
        log.debug('onAuthenticated.loadErrorDictionary: failed to get the error dictionary', errorResponse);
        log.setDictionaryMessage(2);
      });
  }

  onAuthenticationFailed(error) {
    log.debug('onAuthenticationFailed(): ', this.props, arguments);
    delete localStorage.uuid;
    delete localStorage.mvrCustomerId;
    delete sessionStorage.token;
    this.setState({loggedIn: false});
    this.props.onAuthenticationComplete(true, null);

    // if AI enabled and auth fails, redirect to base route to hit AI login page
    if (enableAvalaraIdentity) {
      window.location.href = 'https://www.avalara.com/us/en/signin.html'
    }
  }
  onLogoutSuccess = (...args) => {
    const [idToken] = args;
    log.debug('onLogoutSuccess(): ', this.props, args);
    delete localStorage.uuid;
    delete localStorage.mvrCustomerId;
    delete sessionStorage.token;
    window.appConfig.featureFlags = {};
    this.props.onLogoutComplete();

    // Note: route is not updated when AI is enabled because the Header will navigate to /auth/logout
    if (!enableAvalaraIdentity && !enableAvalaraIdentityJwt) {
      store.dispatch(push('/'))
    };

    if (enableAvalaraIdentityJwt) {
      const redirectUrl = `${aiEndSessionUrl}?post_logout_redirect_uri=${window.location.origin}&id_token_hint=${idToken}`;
      window.location.href = redirectUrl;
      return;
    }

    this.setState({loggedIn: false});
  }

  handleIdleTimeout = async () => {
      store.dispatch(AUTHENTICATION_MODULE.doAttemptLogout(this.onTimeoutSuccess, (error) => {
          log.error('attemptLogout(): FAILED TO LOG OUT AFTER TIMEOUT: ', error);
      }));
  }

  onTimeoutSuccess = (...args) => {
    const [idToken] = args;
    log.debug('onTimeoutSuccess()');
    delete localStorage.uuid;
    window.appConfig.featureFlags = {};
    this.props.onLogoutComplete();
    const redirectUrl = `${aiEndSessionUrl}?post_logout_redirect_uri=${window.location.origin}&id_token_hint=${idToken}`;
    window.location.href = redirectUrl;
  }

  // Simple check to make sure the access token exists and is valid.
  // This prevents us from making API calls that we know will fail (sessions etc.).
  isJwtValid() {
    const accessToken = getAccessToken();
    if (accessToken == undefined) {
      return false;
    }

    try {
      const decoded = jwtDecode(accessToken);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch (error) {
      log.debug('isJwtValid: failed to decode token', error);
      return false;
    }
  }
}



export default SaaSVrWebApp;
